import React, { useState } from 'react';
import { Box, AppBar, Toolbar, Typography, IconButton, Button, Stack, Avatar, Tooltip, Badge, useTheme, useMediaQuery } from '@mui/material';
import AppBackground from './AppBackground';
import {
  Dashboard as DashboardIcon,
  People as ClientIcon,
  ElectricBolt as UtilityIcon,
  RequestQuote as QuoteIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Business as SuppliersIcon,
  Notifications as NotificationsIcon,
  Campaign as MarketingIcon,
  Support as SupportIcon
} from '@mui/icons-material';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useApi } from '../../hooks/useApi';
import UserProfileDialog from '../User/UserProfileDialog';
import NotificationCenter from '../notifications/NotificationCenter';
import SystemNotificationBanner from '../notifications/SystemNotificationBanner';
import SystemNotificationBell from '../notifications/SystemNotificationBell';
import UserProfileDrawer from '../profile/UserProfileDrawer';
import MobileNavigation from '../MobileNavigation';

const UtilitiesLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { unreadCount } = useNotification();
  const api = useApi();
  
  // Check if user is a sub-broker (role 'user' with subBrokerLevel set)
  const isSubBroker = user?.role === 'user' && user?.subBrokerLevel;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false);

  const handleNavigation = (path: string) => {
    if (path.startsWith('/utilities/admin')) {
      if (user?.role === 'administrator') {
        navigate(path);
      } else {
        navigate('/utilities/dashboard');
      }
    } else {
      navigate(path);
    }
  };

  const handleSupportClick = async () => {
    console.log('[SUPPORT] === Support button clicked ===');
    console.log('[SUPPORT] Step 1: Button click handler started');
    
    try {
      console.log('[SUPPORT] Step 2: Entering try block');
      
      // Collect context
      console.log('[SUPPORT] Step 3: Collecting context data');
      const context = {
        currentUrl: window.location.href,
        pathname: window.location.pathname,
        browser: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        timestamp: new Date().toISOString()
      };
      console.log('[SUPPORT] Step 4: Context collected:', context);

      // Check if api object exists
      console.log('[SUPPORT] Step 5: Checking api object:', {
        apiExists: !!api,
        apiType: typeof api,
        apiPostExists: !!(api && api.post),
        apiPostType: api && typeof api.post
      });

      if (!api || !api.post) {
        console.error('[SUPPORT] ERROR: api.post is not available!');
        throw new Error('API service not available');
      }

      // Call backend to create support session using useApi hook
      console.log('[SUPPORT] Step 6: About to call api.post("/support/session")');
      console.log('[SUPPORT] Step 6a: Endpoint: /support/session');
      console.log('[SUPPORT] Step 6b: Payload:', { context });
      
      let response;
      try {
        console.log('[SUPPORT] Step 7: Executing api.post...');
        response = await api.post<{ supportUrl: string; sessionId: string }>(
          '/support/session', 
          { context }
        );
        console.log('[SUPPORT] Step 8: api.post completed successfully');
      } catch (apiError) {
        console.error('[SUPPORT] ERROR in api.post:', apiError);
        console.error('[SUPPORT] API Error type:', typeof apiError);
        console.error('[SUPPORT] API Error details:', {
          message: apiError instanceof Error ? apiError.message : 'Unknown',
          name: apiError instanceof Error ? apiError.name : 'Unknown',
          stack: apiError instanceof Error ? apiError.stack : 'No stack',
          fullError: apiError
        });
        throw apiError;
      }

      console.log('[SUPPORT] Step 9: Checking response');
      console.log('[SUPPORT] Step 9a: Response object:', response);
      console.log('[SUPPORT] Step 9b: Response type:', typeof response);
      console.log('[SUPPORT] Step 9c: Response.data:', response?.data);
      console.log('[SUPPORT] Step 9d: Response.error:', response?.error);
      console.log('[SUPPORT] Step 9e: Response.loading:', response?.loading);

      if (!response) {
        console.error('[SUPPORT] ERROR: Response is null/undefined');
        throw new Error('No response from server');
      }

      if (response.error) {
        console.error('[SUPPORT] ERROR: Response contains error:', response.error);
        throw new Error(`API Error: ${response.error}`);
      }

      if (!response.data) {
        console.error('[SUPPORT] ERROR: Response.data is null/undefined');
        throw new Error('No data in response');
      }

      console.log('[SUPPORT] Step 10: Checking for supportUrl in response.data');
      if (!response.data.supportUrl) {
        console.error('[SUPPORT] ERROR: No supportUrl in response.data:', response.data);
        throw new Error('No support URL returned from server');
      }

      // Open support portal in new tab
      console.log('[SUPPORT] Step 11: All checks passed, opening in new tab:', response.data.supportUrl);
      console.log('[SUPPORT] Step 11a: Session ID:', response.data.sessionId);
      console.log('[SUPPORT] Step 12: Executing window.open...');
      window.open(response.data.supportUrl, '_blank', 'noopener,noreferrer');
      console.log('[SUPPORT] Step 13: New tab opened successfully');
    } catch (error) {
      console.error('[SUPPORT] === CAUGHT ERROR ===');
      console.error('[SUPPORT] Error type:', typeof error);
      console.error('[SUPPORT] Error instanceof Error:', error instanceof Error);
      console.error('[SUPPORT] Error message:', error instanceof Error ? error.message : 'Not an Error object');
      console.error('[SUPPORT] Error name:', error instanceof Error ? error.name : 'N/A');
      console.error('[SUPPORT] Error stack:', error instanceof Error ? error.stack : 'No stack');
      console.error('[SUPPORT] Full error object:', error);
      console.error('[SUPPORT] Error JSON:', JSON.stringify(error, null, 2));
      
      alert(`Failed to open support portal: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
    
    console.log('[SUPPORT] === Handler function completed ===');
  };

  // Full menu for desktop
  // Sub-brokers only see their dashboard - no other menu items
  const desktopMenuItems = isSubBroker ? [] : [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/utilities/dashboard' },
    { text: 'Contacts', icon: <ClientIcon />, path: '/utilities/leads' },
    { text: 'Clients', icon: <ClientIcon />, path: '/utilities/clients' },
    { text: 'Quote Tool', icon: <QuoteIcon />, path: '/utilities/quotes' },
    { text: 'Contracts', icon: <UtilityIcon />, path: '/utilities/contracts' },
    { text: 'Suppliers', icon: <SuppliersIcon />, path: '/utilities/suppliers' },
    { text: 'Marketing', icon: <MarketingIcon />, path: '/utilities/marketing/subscribers' },
    { text: 'Company Checker', icon: <SearchIcon />, path: '/utilities/company-checker' },
    ...(user?.role === 'administrator' ? [
      { text: 'Admin', icon: <SettingsIcon />, path: '/utilities/admin/lookups' },
    ] : [])
  ];

  // Streamlined menu for mobile
  // Sub-brokers only see their dashboard - no other menu items
  const mobileMenuItems = isSubBroker ? [] : [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/utilities/dashboard' },
    { text: 'Clients', icon: <ClientIcon />, path: '/utilities/clients' },
    { text: 'Quote Tool', icon: <QuoteIcon />, path: '/utilities/quotes' },
    { text: 'Contracts', icon: <UtilityIcon />, path: '/utilities/contracts' },
    { text: 'Marketing', icon: <MarketingIcon />, path: '/utilities/marketing/subscribers' },
    ...(user?.role === 'administrator' ? [
      { text: 'Admin', icon: <SettingsIcon />, path: '/utilities/admin/lookups' },
    ] : [])
  ];

  const menuItems = isMobile ? mobileMenuItems : desktopMenuItems;


  // Render mobile navigation if on mobile
  if (isMobile) {
    return (
      <AppBackground>
        <MobileNavigation user={user ? { 
          name: `${user.firstName} ${user.lastName}`, 
          email: user.email || '',
          role: user.role,
          subBrokerLevel: user.subBrokerLevel
        } : undefined} />
        {user && (
          <>
            {/* System Notification Banner - positioned before content */}
            <SystemNotificationBanner
              appId="utilities-crm-live"
              clientId="revo"
              environment={process.env.NODE_ENV === 'production' ? 'production' : 'development'}
              userId={user._id}
              userRole={user.role}
            />
          </>
        )}
        <Box sx={{ pt: 6, px: 1, pb: 10 }}>
          <Outlet />
        </Box>
        {user && (
          <>
            <NotificationCenter 
              open={notificationDrawerOpen} 
              onClose={() => setNotificationDrawerOpen(false)} 
            />
            <UserProfileDrawer
              open={profileDrawerOpen}
              onClose={() => setProfileDrawerOpen(false)}
              onOpenProfileDialog={() => setIsProfileDialogOpen(true)}
            />
            <UserProfileDialog
              open={isProfileDialogOpen}
              onClose={() => setIsProfileDialogOpen(false)}
              user={user}
            />
          </>
        )}
      </AppBackground>
    );
  }

  // Desktop layout
  return (
    <AppBackground>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{
            backgroundColor: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(8px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Utilities CRM
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="inherit">
              {user?.firstName} {user?.lastName}
            </Typography>
            
            {/* Support Button */}
            <Tooltip title="Log Support Issue">
              <IconButton
                size="small"
                color="inherit"
                onClick={handleSupportClick}
              >
                <SupportIcon />
              </IconButton>
            </Tooltip>
            
            {/* System Notifications Bell */}
            {user && !isSubBroker && (
              <SystemNotificationBell
                appId="utilities-crm-live"
                clientId="revo"
                environment={process.env.NODE_ENV === 'production' ? 'production' : 'development'}
                userId={user._id}
                userRole={user.role}
              />
            )}
            
            {/* User Profile Avatar */}
            <Tooltip title="Profile">
              <IconButton color="inherit" onClick={() => {
            setProfileDrawerOpen(!profileDrawerOpen);
          }}>
              <Avatar 
                sx={{ width: 32, height: 32, bgcolor: 'primary.dark', cursor: 'pointer' }}
              >
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
              </IconButton>
            </Tooltip>
            
          </Stack>
        </Toolbar>
      </AppBar>

      <Box sx={{ mt: 8, px: 3 }}>
        <Box sx={{ py: 2 }}>
          <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 1, 
              width: '100%',
              justifyContent: 'space-evenly',
              mx: 'auto',
              mb: 3
            }}>
            {menuItems.map((item) => (
              <Button
                key={item.text}
                variant={location.pathname.startsWith(item.path) ? 'contained' : 'outlined'}
                startIcon={item.icon}
                onClick={() => handleNavigation(item.path)}
                size="large"
                sx={{
                  backdropFilter: 'blur(8px)',
                  borderColor: location.pathname.startsWith(item.path) ? 'transparent' : 'rgba(255, 255, 255, 0.2)',
                  backgroundColor: location.pathname.startsWith(item.path) ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: location.pathname.startsWith(item.path) ? '#2563eb' : 'rgba(255, 255, 255, 0.15)',
                    borderColor: location.pathname.startsWith(item.path) ? 'transparent' : 'rgba(255, 255, 255, 0.3)',
                  },
                  padding: '10px 16px',
                  fontSize: '0.95rem',
                  fontWeight: 'medium',
                  minWidth: '120px',
                  flex: '1 1 auto',
                  maxWidth: '180px',
                  height: '48px',
                  textTransform: 'none',
                }}
              >
                {item.text}
              </Button>
            ))}
          </Box>
        </Box>

        <Box 
          sx={{ 
            py: 3,
            px: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            color: 'white',
            mt: 2,
          }}
        >
          <Outlet />
        </Box>
      </Box>
      {user && (
        <>
          {/* System Notification Banner */}
          <SystemNotificationBanner
            appId="utilities-crm-live"
            clientId="revo"
            environment={process.env.NODE_ENV === 'production' ? 'production' : 'development'}
            userId={user._id}
            userRole={user.role}
          />
          
          <NotificationCenter 
            open={notificationDrawerOpen} 
            onClose={() => setNotificationDrawerOpen(false)} 
          />
          
          {/* User Profile Drawer */}
          <UserProfileDrawer
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
        onOpenProfileDialog={() => setIsProfileDialogOpen(true)}
      />
      
      <UserProfileDialog
          open={isProfileDialogOpen}
          onClose={() => setIsProfileDialogOpen(false)}
          user={user}
        />
        </>
      )}
    </Box>
    </AppBackground>
  );
};

export default UtilitiesLayout;
